import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#F4F6F9',
    fontFamily: 'system-ui, sans-serif',
  },
  main: {
    marginLeft: 220,
    padding: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#888',
    marginBottom: 24,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '0.5px solid #e0e0e0',
    padding: 24,
    maxWidth: 600,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: '1px solid #eee',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: '600',
    color: '#555',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: '1px solid #ddd',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    height: 120,
    resize: 'vertical',
  },
  recipientGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginBottom: 16,
  },
  recipientBtn: {
    padding: '8px',
    borderRadius: 8,
    border: '1.5px solid #ddd',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: 'white',
    color: '#888',
    textAlign: 'center',
  },
  recipientBtnActive: {
    padding: '8px',
    borderRadius: 8,
    border: '1.5px solid #1D6FA4',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#E6F1F9',
    color: '#1D6FA4',
    textAlign: 'center',
  },
  clientList: {
    border: '1px solid #eee',
    borderRadius: 8,
    maxHeight: 200,
    overflowY: 'auto',
    marginBottom: 16,
  },
  clientItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '0.5px solid #f0f0f0',
    gap: 10,
    cursor: 'pointer',
  },
  submitBtn: {
    backgroundColor: '#E8620A',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontSize: 15,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 8,
  },
  success: {
    backgroundColor: '#EAF3DE',
    color: '#3B6D11',
    padding: '12px 16px',
    borderRadius: 8,
    fontSize: 14,
    marginBottom: 16,
    fontWeight: '600',
  },
  previewCard: {
    backgroundColor: '#F4F6F9',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
    border: '1px solid #e0e0e0',
  },
  previewLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#888',
    letterSpacing: 2,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  previewSubject: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 6,
  },
  previewBody: {
    fontSize: 14,
    color: '#333',
    lineHeight: 1.6,
  },
};

export default function Broadcast() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [recipients, setRecipients] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState('all');

  const agentId = auth.currentUser?.uid;

  useEffect(() => {
    const fetchClients = async () => {
      if (!agentId) return;
      const q = query(collection(db, 'clients'), where('agentId', '==', agentId));
      const snap = await getDocs(q);
      setClients(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchClients();
  }, [agentId]);

  const toggleClient = (id) => {
    setSelectedClients(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const getRecipientCount = () => {
    if (recipients === 'all') return clients.length;
    if (recipients === 'supplement') return clients.filter(c => c.planType === 'Supplement').length;
    if (recipients === 'advantage') return clients.filter(c => c.planType === 'Advantage').length;
    if (recipients === 'selected') return selectedClients.length;
    return 0;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject || !body) return;
    setLoading(true);
    try {
      const functions = getFunctions();
      const sendBroadcast = httpsCallable(functions, 'sendBroadcastMessage');
      const result = await sendBroadcast({
        recipients,
        selectedClientIds: selectedClients,
        subject,
        body,
      });
      const { successCount, failCount } = result.data;
      setSuccessMessage(`Sent to ${successCount} client${successCount !== 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed — no push token)` : ''}.`);
      setSubject('');
      setBody('');
      setSelectedClients([]);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Broadcast error:', err);
      setSuccessMessage('Error sending message. Please try again.');
      setTimeout(() => setSuccessMessage(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (planFilter === 'all') return true;
    return c.planType === planFilter;
  });

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Send Message</div>
        <div style={styles.pageSub}>Send a message to your clients. They will receive a push notification.</div>

        <div style={styles.form}>
          {successMessage && <div style={styles.success}>{successMessage}</div>}

          <form onSubmit={handleSend}>
            <div style={styles.sectionTitle}>Send To</div>
            <div style={styles.recipientGrid}>
              {['all', 'supplement', 'advantage', 'selected'].map(r => (
                <button key={r} type="button"
                  style={recipients === r ? styles.recipientBtnActive : styles.recipientBtn}
                  onClick={() => setRecipients(r)}>
                  {r === 'all' ? 'All Clients' :
                   r === 'supplement' ? 'Supplement Only' :
                   r === 'advantage' ? 'Advantage Only' : 'Select Clients'}
                </button>
              ))}
            </div>

            {recipients === 'selected' && (
              <div style={styles.field}>
                <label style={styles.label}>Select Clients</label>
                <div style={styles.clientList}>
                  {filteredClients.map(client => (
                    <div key={client.id} style={styles.clientItem}
                      onClick={() => toggleClient(client.id)}>
                      <input type="checkbox" readOnly
                        checked={selectedClients.includes(client.id)} />
                      <span style={{ fontSize: 13, color: '#333' }}>{client.name}</span>
                      <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>
                        {client.planType}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={styles.sectionTitle}>Message</div>
            <div style={styles.field}>
              <label style={styles.label}>Subject Line</label>
              <input style={styles.input} value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Important Update" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Message</label>
              <textarea style={styles.textarea} value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Type your message here..." required />
            </div>

            {(subject || body) && (
              <div style={styles.previewCard}>
                <div style={styles.previewLabel}>Preview</div>
                {subject && <div style={styles.previewSubject}>{subject}</div>}
                {body && <div style={styles.previewBody}>{body}</div>}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <button style={styles.submitBtn} type="submit" disabled={loading}>
                {loading ? 'Sending...' : `Send to ${getRecipientCount()} Client${getRecipientCount() !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}